import React, { useState } from "react";

import { API } from "aws-amplify";

import { Button, Confirm, useNotify, useRedirect } from "react-admin";

import { makeStyles } from "@material-ui/core/styles";
import { alpha } from "@material-ui/core/styles/colorManipulator";
import { Tooltip } from "@material-ui/core";
import classnames from "classnames";

import { DeleteIcon } from "../../../components/common/iconography";
import { logError } from "../../../utils/appUtils";
import { clearCarrierIdentityCache } from "../../../utils/carrierIdentity";
import { invalidateCarrierList } from "../../../utils/carrierListCache";

import { CarrierDeleteBlocker, findCarrierDeleteBlocker } from "./carrierUtils";

const useStyles = makeStyles(
  (theme) => ({
    deleteButton: {
      color: theme.palette.error.main,
      "&:hover": {
        backgroundColor: alpha(theme.palette.error.main, 0.12),
        // Reset on mouse devices
        "@media (hover: none)": {
          backgroundColor: "transparent",
        },
      },
    },
  }),
  { name: "RaDeleteWithConfirmButton" },
);

// Permanently removes the Carrier record from the database (true DB delete),
// taking its user memberships with it. For carriers created in error — a typo'd
// form, a wrong CSV row — where archiving is not enough: the uniqueness checks
// span archived rows, so an archived mistake goes on holding its email address
// and regulatory numbers against the carrier that really owns them.
//
// The delete itself is the carrierActions lambda's (POST /carrierDelete): it
// retires every membership (the update the Cognito-cleanup stream watches),
// removes the rows, deletes the carrier — whose stream record releases the
// company's identifier claims — and sweeps anything the colleague cascade wrote
// mid-flight. One invocation, so a closed laptop cannot strand a half-deleted
// carrier, and its shipment gate reads DynamoDB itself — rows this session's
// groups would hide from an AppSync probe still count.
//
// The click-time check here is the same gate asked client-side, for the answer
// before the dialog rather than after it. The lambda re-asks authoritatively.
export const DeepDeleteButton = ({ record, className, backPath, ...props }) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  // Set once something has been found to stand in the way, so the button stays
  // off with the reason on it rather than inviting the same refusal again.
  const [blocker, setBlocker] = useState(null);

  const handleDialogClose = () => setOpen(false);

  const refuse = (message) => {
    setBlocker(message);
    notify(message, "warning");
  };

  const handleDialogOpen = async () => {
    setIsChecking(true);
    try {
      const found = await findCarrierDeleteBlocker(record.id);
      if (found) {
        refuse(found);
        return;
      }
      setOpen(true);
    } catch (error) {
      logError(
        "managerApp:carriers:DeepDeleteButton:findCarrierDeleteBlocker",
        error,
        false,
      );
      // An unanswered check is not a clean carrier. Fail closed: the delete is
      // the one thing here that cannot be taken back.
      refuse(CarrierDeleteBlocker.UNKNOWN);
    } finally {
      setIsChecking(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await API.post("transREST", "/carrierDelete", {
        // The yard goes with the id: the lambda settles standing against it
        // before it reads the carrier, so its answer says nothing about
        // carriers outside the yard the caller manages.
        body: { carrierId: record.id, reloadYardId: record.reloadYardId },
      });

      // The dropdown cache still offers the carrier, and the on-blur identity
      // book still calls its email and numbers duplicates.
      invalidateCarrierList();
      clearCarrierIdentityCache();

      notify("Carrier and its users deleted.");
      redirect("/carriers");
    } catch (error) {
      // 409 is the lambda's own gate refusing — shipment history the client
      // probe could not see, or a driver enrolled since the check above. Its
      // message is written for the manager, so it is shown as-is.
      const refusal =
        error?.response?.status === 409 && error?.response?.data?.error;
      if (refusal) {
        setOpen(false);
        refuse(refusal);
        return;
      }
      // Anything else: the lambda's steps are idempotent and ordered so a
      // retry finishes the job — reported through logError's notify.
      logError(
        "managerApp:carriers:DeepDeleteButton:handleDelete",
        error,
        notify,
      );
    } finally {
      setIsDeleting(false);
      setOpen(false);
    }
  };

  return (
    <>
      {/* Tooltip requires a span wrapper because MUI tooltips don't fire on disabled buttons */}
      <Tooltip title={blocker || ""}>
        <span>
          <Button
            {...props}
            label="Delete"
            disabled={!!blocker || isChecking || isDeleting}
            onClick={handleDialogOpen}
            className={classnames(
              "ra-delete-button",
              classes.deleteButton,
              className,
            )}
          >
            <DeleteIcon />
          </Button>
        </span>
      </Tooltip>
      <Confirm
        isOpen={open}
        loading={isDeleting}
        title="Delete Confirmation"
        content="Are you sure you want to permanently delete this carrier? Its users lose their access here — and any whose only transload this is lose their sign-in too. This action cannot be undone."
        onConfirm={handleDelete}
        onClose={handleDialogClose}
      />
    </>
  );
};
